import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  usePracticePlanQueryParams,
  useChallengeFilterOptions,
  useDifficulties,
} from 'src/hooks';
import { useDebouncedValue } from 'src/hooks/useDebounce';
import { FilterTrait } from 'src/models/SessionFilters';
import { Tag } from 'src/models/Tag';
import { DurationRange2 as DurationRange } from 'src/models/DurationRange';
import { PracticePlanFilters } from 'src/models/PracticePlan';
import { Difficulty } from 'src/models/Difficulty';
import { Coach } from 'src/models/Coach';
import { FilterOption } from 'src/features/SessionSearchbox/types';
import { PracticePlanQuery } from 'src/models/PracticePlan';
import { ChallengeType } from 'src/models/Challenge';

const debounceDelay = 500;

interface PracticePlanSearch {
  displayFilters: boolean;
  toggleDisplayFilters: VoidFunction;
  searchLink: string;
  values: {
    all: FilterOption[];
    aggregated: Tag[];
    tag: Tag[];
    duration: DurationRange[];
    difficulty: Difficulty[];
    coach: Coach[];
    phrase: string;
  };
  options: {
    tag: Tag[];
    duration: DurationRange[];
    difficulty: Difficulty[];
    coach: Coach[];
  };
  aggregatedOptions: Tag[];
  onChange: {
    tag: (coaches: Tag[]) => void;
    duration: (coaches: DurationRange[]) => void;
    difficulty: (coaches: Difficulty[]) => void;
    coach: (coaches: Coach[]) => void;
    phrase: (phrase: string) => void;
    liked: (liked: boolean) => void;
  };
  onPhraseConfirm: (phrase: string, options?: { triggerSearch: boolean }) => void;
  onClearFilter: (item: FilterOption, index: number) => void;
  onClearAllFilters: () => void;
}

function adjustOptions<T extends { [K: string]: any }>(values: string[] | undefined, options: T[], key: keyof T) {
  if (values == null || options.length === 0) {
    return [];
  }
  const map = new Map<string, T>(options.map(v => ([v[key], v])));
  return values.map(v => map.get(v)!);
}

function stripPracticePlanFilters(filters: PracticePlanFilters): PracticePlanFilters {
  const { coach, duration, ...rest } = filters;
  return rest;
}

const usePracticePlanSearch = () => {
  const history = useHistory();
  const query = usePracticePlanQueryParams();
  const [page] = useState(query.page);
  const [filters, setFilters] = useState<PracticePlanFilters>(
    () => stripPracticePlanFilters(query.filters ?? {}),
  );
  useEffect(
    () => setFilters(stripPracticePlanFilters(query.filters ?? {})),
    [query],
  );

  const [displayFilters, setDisplayFilters] = useState(false);
  const toggleDisplayFilters = () => setDisplayFilters(true);

  const searchLink = useMemo(() => {
    const search = PracticePlanQuery.serializeToUrlParams({
      filters,
      ...(page ? { page } : {}),
    });
    return `/practice-plans?${search}`;
  }, [filters, page]);
  const liveSearchLink = useDebouncedValue(searchLink, { debounceDelay });
  useEffect(() => {
    if (liveSearchLink) {
      history.replace(liveSearchLink);
    }
  }, [liveSearchLink, history]);

  const { tag: scopedTags } = useChallengeFilterOptions(ChallengeType.PracticePlan);
  const allDifficulties = useDifficulties();

  const options = useMemo(
    () => ({
      coach: [] as Coach[],
      tag: scopedTags,
      duration: [] as DurationRange[],
      difficulty: allDifficulties,
    }),
    [scopedTags, allDifficulties],
  );

  const aggregatedOptions = useMemo(
    () => [...options.tag].sort((a, b) => a.name.localeCompare(b.name)),
    [options.tag],
  );

  const onFiltersChange = useCallback((trait: Exclude<keyof PracticePlanFilters, 'liked'>, values: string[]) => {
    setFilters(filters => {
      filters[trait] = values.length > 0 ? values : undefined;
      return stripPracticePlanFilters({ ...filters });
    });
  }, []);

  const onPhraseConfirm = useCallback((phrase: string) => {
    onFiltersChange(FilterTrait.Phrase, phrase === '' ? [] : [phrase]);
  }, [onFiltersChange]);

  const onClearAllFilters = useCallback(() => {
    setFilters(() => ({}));
  }, []);

  const values = useMemo(() => {
    const out = {
      tag: adjustOptions(filters.tag, options.tag, 'id'),
      duration: [] as DurationRange[],
      difficulty: adjustOptions(filters.difficulty, options.difficulty, 'value'),
      coach: [] as Coach[],
    };
    const all = [...out.tag, ...out.difficulty];
    const phrase = filters.phrase ? filters.phrase[0] : '';
    return { all, aggregated: out.tag, ...out, phrase } as PracticePlanSearch['values'];
  }, [filters, options]);

  const onClearFilter = useCallback((item: FilterOption, _: number) => {
    const key = item.trait as 'tag' | 'difficulty';
    if (Array.isArray(values[key])) {
      const items = (values[key] as Array<Tag | Difficulty>)
        .filter(v => v !== item)
        .map(v => ('id' in v ? v.id : String(v.value)));
      onFiltersChange(key, items);
    }
  }, [values, onFiltersChange]);

  useEffect(() => {
    setDisplayFilters(false);
  }, [history.location.pathname]);

  return useMemo<PracticePlanSearch>(() => ({
    options,
    searchLink,
    aggregatedOptions,
    displayFilters,
    toggleDisplayFilters,
    values,
    onChange: {
      tag: (tags) =>
        onFiltersChange(FilterTrait.Tag, tags.map(v => v.id)),
      duration: () => {},
      difficulty: (difficulties) =>
        onFiltersChange(FilterTrait.Difficulty, difficulties.map(v => String(v.value))),
      coach: () => {},
      phrase: (phrase) =>
        onFiltersChange(FilterTrait.Phrase, phrase === '' ? [] : [phrase]),
      liked: (liked) => {
        setFilters(filters => stripPracticePlanFilters({
          ...filters,
          liked: liked ? true : undefined,
        }));
      },
    },
    onPhraseConfirm,
    onClearAllFilters,
    onClearFilter,
  } as PracticePlanSearch), [
    options,
    searchLink,
    aggregatedOptions,
    values,
    onFiltersChange,
    onPhraseConfirm,
    onClearAllFilters,
    onClearFilter,
    displayFilters,
  ]);
};

export default usePracticePlanSearch;
